<template>
    <div
        class="form-group has-effect"
        :class="[
            { 'is-invalid': invalid || localInvalid || onlyNumbersInvalid },
            { 'no-icon': !invalidIcon },
            { disabled: disabled }
        ]"
    >
        <div class="has-effect-input">
            <input
                :type="type"
                class="form-control form-input"
                :class="[
                    { 'has-content': text || localText || onlyNumbersInvalid },
                    { 'is-invalid': invalid },
                    { 'has-icon': tooltip },
                    customClass
                ]"
                v-model="localText"
                v-mask="{ mask: mask, greedy: true }"
                :id="name"
                :name="name"
                :readonly="readonly"
                :required="required"
                :placeholder="placeholder"
                :min="type === 'number' ? min : null"
                :disabled="disabled"
                @keydown="onKeyPress"
                @blur="val => $emit('blur', val)"
                @keyup="val => $emit('keyup', val)"
                @change="val => $emit('change', val)"
            />

            <label v-if="label" class="form-label" :for="name">
                <span v-if="label" v-text="label" />
                <span v-if="required" v-text="'*'" class="text-danger pl-1" />
            </label>

            <button
                v-if="type == 'password'"
                @click="showPass"
                type="button"
                class="d-flex input-icon"
            >
                <a-icon v-if="passVisible" icon="Eye" />

                <a-icon v-else icon="EyeClosed" />
            </button>

            <button
                v-if="tooltip"
                type="button"
                class="d-flex input-icon"
                v-tooltip="tooltip"
            >
                <a-icon icon="Info" />
            </button>
        </div>
        <div
            v-if="onlyNumbersInvalid || invalidFeedback || localInvalid"
            class="invalid-feedback text-left"
        >
            {{ onlyNumbersInvalid || invalidFeedback || localInvalid }}
        </div>
    </div>
</template>

<script>
import DOMPurify from 'dompurify'

export default {
    props: {
        required: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
            default: 'text'
        },
        label: {
            type: String,
            required: false
        },
        name: {
            type: String,
            required: false
        },
        text: {
            type: String,
            required: false
        },
        invalid: {
            type: Boolean,
            default: false
        },
        invalidFeedback: {
            type: String,
            default: 'Wystąpił błąd'
        },
        invalidIcon: {
            type: Boolean,
            default: true
        },
        readonly: {
            type: Boolean,
            default: false
        },
        format: {
            type: String,
            required: false
        },
        customClass: {
            type: String,
            required: false
        },
        placeholder: {
            type: String,
            required: false
        },
        min: {
            type: Number
        },
        max: {
            type: Number
        },
        tooltip: {
            type: String,
            required: false
        },
        preventDecimals: {
            type: Boolean,
            default: false
        },
        validation: {
            type: String,
            required: false
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },

    watch: {
        text: {
            immediate: true,
            handler(val) {
                if (val === this.localText) return
                this.localText = val
            }
        },
        localText: {
            handler(val) {
                const sanitizedVal = DOMPurify.sanitize(val)
                if (this.validation === 'localCode') {
                    const newVal = this.localCodeValidation(sanitizedVal)
                    this.localText = newVal
                    this.$emit('input', newVal)
                } else if (this.type === 'number') {
                    const newVal = this.validateMinMax(sanitizedVal)
                    this.localText = newVal
                    this.$emit('input', newVal)
                } else {
                    this.$emit('input', sanitizedVal)
                }
            }
        }
    },

    data() {
        return {
            localText: '',
            mask: null,
            passVisible: false,
            localInvalid: '',
            onlyNumbersInvalid: ''
        }
    },
    methods: {
        showPass() {
            if (this.type === 'password') {
                this.type = 'text'
                this.passVisible = true
            } else {
                this.type = 'password'
                this.passVisible = false
            }
        },
        validateMinMax(val) {
            if (+val < +this.min) {
                this.localInvalid = `Pole może zawierać jedynie wartość liczbową nie niższą niż ${this.min.toString()} (bez znaków specjalnych)`
                return this.min.toString()
            } else if (+val > +this.max) {
                this.localInvalid = `Pole może zawierać jedynie wartość liczbową nie przekraczającą ${this.max.toString()} (bez znaków specjalnych)`
                return this.max.toString()
            } else {
                setTimeout(() => {
                    this.localInvalid = ''
                }, 5000)

                return val !== '0' ? val.replace(/^0+/, '') : val
            }
        },
        localCodeValidation(str = '') {
            if (!str) str = ''
            str = str
                .replace(/[^0-9]/g, '')
                .substring(0, str.startsWith('2') ? 7 : 13)
            return str
        },
        onKeyPress(e) {
            e = e || window.event
            const charCode = e.which ? e.which : e.keyCode
            if (
                (this.type === 'number' || this.validation === 'localCode') &&
                charCode > 31 &&
                (charCode < 48 || charCode > 57) &&
                !(charCode > 36 && charCode < 45) &&
                !(charCode > 93 && charCode < 106) &&
                !e.metaKey &&
                !e.ctrlKey &&
                !e.shiftKey &&
                charCode !== 46 &&
                charCode !== 188 &&
                charCode !== 189 &&
                charCode !== 108 &&
                charCode !== 110 &&
                charCode !== 190 &&
                charCode !== 229
            ) {
                if (this.validation === 'localCode') {
                    this.onlyNumbersInvalid = 'Dopuszczone są tylko cyfry'
                }
                e.preventDefault()
            } else if (+this.min >= 0 && charCode === 189) {
                e.preventDefault()
            } else if (
                this.preventDecimals &&
                (charCode === 188 ||
                    charCode === 190 ||
                    charCode === 108 ||
                    charCode === 110)
            ) {
                e.preventDefault()
            } else {
                if (this.validation === 'localCode') {
                    this.onlyNumbersInvalid = ''
                }
                return true
            }
        }
    }
}
</script>
